// src/context/CacheContext.js
// Global cache context for managing application-wide caching

import React, { createContext, useContext, useCallback, useEffect, useState } from 'react';
import cacheService from '../services/cacheService';
import { idbClear } from '../services/indexedDBStore';

const CacheContext = createContext(null);

/**
 * CacheProvider - Provides global cache management and preloading
 */
export function CacheProvider({ children }) {
  const [isPreloaded, setIsPreloaded] = useState(false);
  const [cacheStats, setCacheStats] = useState(null);

  /**
   * Invalidate cache by type
   */
  const invalidateCache = useCallback((type, params = null) => {
    if (params) {
      cacheService.invalidate(type, params);
    } else {
      cacheService.invalidateType(type);
    }
  }, []);

  /**
   * Invalidate multiple cache types at once
   */
  const invalidateMany = useCallback((types) => {
    types.forEach(type => {
      cacheService.invalidateType(type);
    });
  }, []);

  /**
   * Clear all caches including IndexedDB (useful for logout)
   */
  const clearAllCaches = useCallback(() => {
    cacheService.clearAll();
    setIsPreloaded(false);
  }, []);

  /**
   * Clear only the admin IndexedDB cache (useful for manual reset/debugging)
   */
  const clearAdminCache = useCallback(() => {
    // Invalidate all admin types from memory
    const adminTypes = Object.keys(cacheService.CACHE_CONFIG).filter(
      type => cacheService.useIndexedDB(type)
    );
    adminTypes.forEach(type => cacheService.invalidateType(type));
    // Clear IndexedDB store
    idbClear().catch(() => {});
  }, []);

  /**
   * Prefetch common data into cache
   */
  const preloadCommonData = useCallback(async (fetchFunctions) => {
    const { getMasterServices, getServiceCategories } = fetchFunctions;

    try {
      // Prefetch static data in parallel
      await Promise.all([
        getMasterServices && cacheService.prefetch('masterServices', getMasterServices),
        getServiceCategories && cacheService.prefetch('serviceCategories', getServiceCategories),
      ].filter(Boolean));

      setIsPreloaded(true);
    } catch (error) {
      console.error('[CacheProvider] Preload failed:', error);
    }
  }, []);

  /**
   * Get cache stats (for debugging)
   */
  const refreshCacheStats = useCallback(() => {
    const stats = cacheService.getStats();
    setCacheStats(stats);
    return stats;
  }, []);

  /**
   * Check if data is cached
   */
  const isCached = useCallback((type, params = null) => {
    return cacheService.has(type, params);
  }, []);

  /**
   * Get cached data directly (without triggering fetch)
   */
  const getCachedData = useCallback((type, params = null) => {
    const cached = cacheService.get(type, params);
    return cached?.data ?? null;
  }, []);

  // Cleanup expired entries periodically
  useEffect(() => {
    const cleanup = () => cacheService.cleanupExpired();

    // Initial cleanup
    cleanup();

    // Schedule periodic cleanup
    const intervalId = setInterval(cleanup, 5 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, []);

  // Clear cache on logout (listen for auth changes)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'logout_event') {
        clearAllCaches();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [clearAllCaches]);

  const value = {
    // State
    isPreloaded,
    cacheStats,

    // Methods
    invalidateCache,
    invalidateMany,
    clearAllCaches,
    clearAdminCache,
    preloadCommonData,
    refreshCacheStats,
    isCached,
    getCachedData,

    // Direct access to cache service for advanced usage
    cacheService,
  };

  return (
    <CacheContext.Provider value={value}>
      {children}
    </CacheContext.Provider>
  );
}

/**
 * useCache - Hook to access cache context
 */
export function useCache() {
  const context = useContext(CacheContext);
  if (!context) {
    throw new Error('useCache must be used within a CacheProvider');
  }
  return context;
}

/**
 * useCacheInvalidation - Convenience hook for common invalidation patterns
 */
export function useCacheInvalidation() {
  const { invalidateCache, invalidateMany } = useCache();

  return {
    // Invalidate after customer changes
    invalidateCustomerData: useCallback(() => {
      invalidateMany(['customers', 'stats']);
    }, [invalidateMany]),

    // Invalidate after quote changes
    invalidateQuoteData: useCallback(() => {
      invalidateMany(['quotes', 'stats']);
    }, [invalidateMany]),

    // Invalidate after job changes
    invalidateJobData: useCallback(() => {
      invalidateMany(['jobs', 'stats']);
    }, [invalidateMany]),

    // Invalidate after appointment changes
    invalidateAppointmentData: useCallback(() => {
      invalidateMany(['appointments', 'stats']);
    }, [invalidateMany]),

    // Invalidate after invoice changes
    invalidateInvoiceData: useCallback(() => {
      invalidateMany(['invoices', 'stats']);
    }, [invalidateMany]),

    // Invalidate garage profile data
    invalidateGarageData: useCallback(() => {
      invalidateMany(['garageProfile', 'garageServices', 'garageSettings']);
    }, [invalidateMany]),

    // Invalidate admin data
    invalidateAdminData: useCallback(() => {
      const adminTypes = Object.keys(cacheService.CACHE_CONFIG).filter(
        type => cacheService.useIndexedDB(type)
      );
      invalidateMany(adminTypes);
    }, [invalidateMany]),

    // Invalidate all user data (for logout)
    invalidateAllUserData: useCallback(() => {
      invalidateMany([
        'customers', 'vehicles', 'quotes', 'jobs', 'appointments',
        'invoices', 'stats', 'garageProfile', 'garageServices',
        'garageSettings', 'clientProfile', 'savedGarages',
        // Admin types (cleared on logout)
        'adminGarages', 'adminClients', 'adminUsers', 'adminGarageStats',
        'adminClientStats', 'adminSubscriptions', 'adminClaims', 'adminQuotes',
        'adminConversations', 'adminAnalytics', 'adminDataQuality',
        'adminCRMMetrics', 'adminPipeline', 'adminCommissions',
      ]);
    }, [invalidateMany]),

    // Generic invalidation
    invalidate: invalidateCache,
    invalidateMany,
  };
}

export default CacheContext;
